/**
 * A date-range filter: one pill, presets, and a two-click calendar.
 *
 * ## Why a range on the gallery had to reach the server
 *
 * The photos read is newest-first and capped — it fetches `limit` visits and
 * signs only what it returns. A range filtered in the browser would therefore
 * search the newest page for photos that are not on it, and "March" would
 * come back empty on a workspace with a busy April. So the control's whole
 * output is two URL parameters the server reads (`?from=&to=`), and the
 * gallery re-fetches. That also makes a filtered gallery a LINK somebody can
 * paste, which is most of the point of having it.
 *
 * ## The presets are not decoration
 *
 * Nearly every real question about site photos is "this week" or "since the
 * pour" — a relative window, not two remembered dates. A calendar-only
 * control makes the common case the expensive one: open, orient, find today,
 * count back seven days, click twice. The preset rows answer it in one click
 * and the calendar is there for the rest, which is the split every date filter
 * worth using ends up at.
 *
 * A preset writes CONCRETE days, not a token like `7d`. The label is then
 * recovered by matching the range back against each preset (`presetFor`), so
 * a URL shared on Tuesday still means Tuesday's week when it is opened on
 * Friday. A stored `7d` would silently re-resolve to a different week under
 * the reader, which is the same class of bug as a server deciding "which day"
 * from its own clock.
 *
 * ## Two clicks, and the panel says which one you are on
 *
 * Range selection is: first click sets an end, second click completes it. The
 * failure mode of every two-click calendar is a reader who does not know they
 * are half-way through one, so the footer stops describing the range and
 * starts instructing ("Pick the other day") the moment an anchor is set, and
 * the hovered day paints the range it WOULD make. Clicking backwards is not
 * an error — the pair is sorted, because somebody who clicks the 7th and then
 * the 1st plainly means the 1st to the 7th.
 *
 * Days after today are disabled. A photo cannot be captured in the future, so
 * a selectable tomorrow can only ever produce an empty gallery.
 */

import { useMemo, useRef, useState } from "react";
import { C } from "../tokens";
import { AnchoredPanel } from "./AnchoredPanel";
import { Calendar, isoDay, parseDay, type DayRange } from "./Calendar";
import { MONTHS, META } from "./chrome";

export type { DayRange };

/** Local midnight, `days` from `date`. */
function shiftDay(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

/** Local midnight of the 1st of `date`'s month. */
function monthStart(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

/**
 * The presets, resolved against the reader's today.
 *
 * "This month" and "Last month" earn their place over more week variants:
 * a monthly report is the other thing people actually pull photos for.
 */
export function presetsFor(today: Date): Array<{ id: string; label: string; range: DayRange }> {
  const startOfThisMonth = monthStart(today);
  const endOfLastMonth = shiftDay(startOfThisMonth, -1);
  return [
    { id: "today", label: "Today", range: { from: isoDay(today), to: isoDay(today) } },
    {
      id: "7d",
      label: "Last 7 days",
      range: { from: isoDay(shiftDay(today, -6)), to: isoDay(today) },
    },
    {
      id: "30d",
      label: "Last 30 days",
      range: { from: isoDay(shiftDay(today, -29)), to: isoDay(today) },
    },
    {
      id: "month",
      label: "This month",
      range: { from: isoDay(startOfThisMonth), to: isoDay(today) },
    },
    {
      id: "lastMonth",
      label: "Last month",
      range: {
        from: isoDay(monthStart(endOfLastMonth)),
        to: isoDay(endOfLastMonth),
      },
    },
  ];
}

/** The preset a range IS, or `null` when it is a custom pair. */
function presetFor(range: DayRange | null, today: Date): string | null {
  if (!range) return null;
  const match = presetsFor(today).find(
    (preset) => preset.range.from === range.from && preset.range.to === range.to,
  );
  return match ? match.id : null;
}

/**
 * `Sep 1 – Sep 7`, or `Sep 7`, or `Dec 20, 2025 – Jan 4` when the range
 * crosses out of this year.
 *
 * The year appears only on the end that needs it. A range inside this year
 * reads as two short dates, which is what fits in a pill.
 */
export function rangeLabel(range: DayRange, today: Date): string {
  const from = parseDay(range.from);
  const to = parseDay(range.to);
  if (!from || !to) return "Custom";
  const stamp = (date: Date) => {
    const base = `${MONTHS[date.getMonth()]} ${date.getDate()}`;
    return date.getFullYear() === today.getFullYear()
      ? base
      : `${base}, ${date.getFullYear()}`;
  };
  return range.from === range.to ? stamp(from) : `${stamp(from)} – ${stamp(to)}`;
}

/** What the pill says: a preset's own name when the range is one. */
export function triggerLabel(range: DayRange | null, today: Date): string {
  if (!range) return "Any time";
  const preset = presetFor(range, today);
  if (preset) {
    const found = presetsFor(today).find((candidate) => candidate.id === preset);
    if (found) return found.label;
  }
  return rangeLabel(range, today);
}

export interface DateRangePickerProps {
  value: DayRange | null;
  onChange: (next: DayRange | null) => void;
  /** Injected in tests; the reader's own today otherwise. */
  today?: Date;
  /** Announced on the trigger — "Filter photos by date". */
  label: string;
  testId?: string;
}

export function DateRangePicker({
  value,
  onChange,
  today: injectedToday,
  label,
  testId = "range",
}: DateRangePickerProps) {
  const today = useMemo(() => {
    const base = injectedToday ?? new Date();
    return new Date(base.getFullYear(), base.getMonth(), base.getDate());
  }, [injectedToday]);

  const [open, setOpen] = useState(false);
  /** The first day of a range being built, before its partner is clicked. */
  const [anchor, setAnchor] = useState<string | null>(null);
  const [hover, setHover] = useState<string | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const active = value !== null;
  const presets = presetsFor(today);
  const currentPreset = presetFor(value, today);
  const todayKey = isoDay(today);

  /**
   * Opening clears any half-made range.
   *
   * Re-centring on the selection used to happen here too; `Calendar` seeds
   * its own month from the band's end, and this panel unmounts when it
   * closes, so reopening on March still lands on March — by the grid's own
   * rule rather than by two components agreeing about it.
   */
  function toggle() {
    if (!open) {
      setAnchor(null);
      setHover(null);
    }
    setOpen(!open);
  }

  function commit(next: DayRange | null) {
    setAnchor(null);
    setHover(null);
    onChange(next);
    setOpen(false);
  }

  /** A calendar click: opens a range, or closes the one in progress. */
  function pickDay(key: string) {
    if (anchor === null) {
      setAnchor(key);
      setHover(key);
      return;
    }
    // Sorted, not rejected — see the file header.
    const pair: DayRange = anchor <= key ? { from: anchor, to: key } : { from: key, to: anchor };
    commit(pair);
  }

  /** The band to paint: the committed range, or the one the cursor implies. */
  const band = useMemo<DayRange | null>(() => {
    if (anchor !== null) {
      const other = hover ?? anchor;
      return anchor <= other ? { from: anchor, to: other } : { from: other, to: anchor };
    }
    return value;
  }, [anchor, hover, value]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="kb"
        data-testid={`${testId}-trigger`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={label}
        onClick={toggle}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 7,
          height: 30,
          padding: active ? "0 6px 0 11px" : "0 11px",
          borderRadius: 999,
          background: active ? C.pendingTint : C.surface,
          border: `1px solid ${active ? C.gold : C.hair}`,
          color: active ? C.ink : C.sub,
          fontSize: 14.95,
          fontWeight: 500,
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        <CalendarGlyph tone={active ? C.goldInk : C.sub2} />
        <span data-testid={`${testId}-label`}>{triggerLabel(value, today)}</span>
        {active ? (
          /* Clearing is the most common thing a reader does to an active
             filter, so it lives ON the pill rather than only inside the
             panel. A span, not a button: a button inside a button is invalid
             HTML and the browser drops one of them. */
          <span
            role="button"
            tabIndex={0}
            data-testid={`${testId}-clear`}
            aria-label="Clear the date filter"
            title="Clear the date filter"
            onClick={(event) => {
              event.stopPropagation();
              commit(null);
            }}
            onKeyDown={(event) => {
              if (event.key !== "Enter" && event.key !== " ") return;
              event.preventDefault();
              event.stopPropagation();
              commit(null);
            }}
            style={{
              display: "grid",
              placeItems: "center",
              width: 18,
              height: 18,
              borderRadius: 999,
              background: C.hairRow,
              color: C.sub,
              cursor: "pointer",
            }}
          >
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />
            </svg>
          </span>
        ) : (
          <Chevron open={open} />
        )}
      </button>

      {open && (
        <AnchoredPanel
          anchorRef={triggerRef}
          onClose={() => {
            setAnchor(null);
            setHover(null);
            setOpen(false);
          }}
          label={label}
          role="dialog"
          /* Measured, not guessed: 8 panel padding + 130 presets + 9 gap + 1
             divider + 9 gap + 248 calendar (a fixed 7x32 grid plus its own 12
             padding) + 8 = 413. The shared grid's columns are a fixed 32px —
             unlike the `1fr` ones this panel used to draw — so a panel sized
             by eye clips Saturday. */
          minWidth={416}
          maxWidth={416}
          testId={`${testId}-panel`}
          panelStyle={{
            padding: 8,
            borderRadius: 14,
            border: `1px solid ${C.popLine}`,
            background: C.surface,
            boxShadow: `0 1px 2px ${C.softShadow}, 0 14px 40px ${C.popShadow}`,
          }}
        >
          {/* Two columns, not two stacked blocks.
              Stacked, the six presets pushed the calendar's last week AND the
              footer below the fold — and a panel taller than the room under
              its trigger is one whose bottom row cannot be clicked, with the
              footer being the half that says which click you are on. Side by
              side, the panel is as tall as the calendar alone and fits
              wherever the pill does. */}
          <div style={{ display: "flex", alignItems: "stretch", gap: 9 }}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                width: 130,
                flexShrink: 0,
              }}
            >
              <PresetRow
                testId={testId}
                label="Any time"
                checked={value === null}
                onSelect={() => commit(null)}
              />
              {presets.map((preset) => (
                <PresetRow
                  key={preset.id}
                  testId={testId}
                  label={preset.label}
                  checked={currentPreset === preset.id}
                  onSelect={() => commit(preset.range)}
                />
              ))}
            </div>

            <div style={{ width: 1, background: C.hairRow, flexShrink: 0 }} />

            <div style={{ flex: 1, minWidth: 0 }}>
              <Calendar
                testId={testId}
                today={today}
                /* The band, which may be the half-made one — see `band`. */
                selection={band}
                onDayClick={pickDay}
                onDayHover={(key) => setHover(key ?? anchor)}
                /* No photo was captured tomorrow, and no month of them. */
                maxDay={todayKey}
                maxMonth={todayKey.slice(0, 7)}
                /* This panel has its own footer: the range in words, and a
                   Clear that also abandons a half-made selection. The
                   calendar's Clear/Today pair would be a second, different
                   answer to the same question three pixels below. */
                footer={false}
              />
            </div>
          </div>

          {/* The footer is the only thing telling a reader they are mid-range,
              so it stops describing and starts instructing. */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 8,
              padding: "0 3px",
              minHeight: 20,
            }}
          >
            <span
              data-testid={`${testId}-footer`}
              style={{ ...META, fontSize: 10.35, color: C.sub2 }}
            >
              {anchor !== null
                ? "Pick the other day"
                : value
                  ? rangeLabel(value, today)
                  : "All photos"}
            </span>
            <span style={{ flex: 1 }} />
            {(value !== null || anchor !== null) && (
              <button
                type="button"
                className="kb"
                data-testid={`${testId}-reset`}
                onClick={() => commit(null)}
                style={{
                  ...META,
                  fontSize: 10.35,
                  color: C.sub,
                  background: "transparent",
                  border: "none",
                  padding: "2px 3px",
                  cursor: "pointer",
                }}
              >
                Clear
              </button>
            )}
          </div>
        </AnchoredPanel>
      )}
    </>
  );
}

function PresetRow({
  testId,
  label,
  checked,
  onSelect,
}: {
  testId: string;
  label: string;
  checked: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      className="kb"
      role="menuitemradio"
      aria-checked={checked}
      data-testid={`${testId}-preset`}
      onClick={onSelect}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        height: 30,
        padding: "0 8px",
        borderRadius: 8,
        border: "none",
        background: checked ? C.propOpen : "transparent",
        color: C.propValue,
        font: "inherit",
        fontSize: 14.95,
        fontWeight: checked ? 600 : 500,
        textAlign: "left",
        cursor: "pointer",
      }}
    >
      <span style={{ flex: 1 }}>{label}</span>
      {checked && (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M5 13l4.5 4.5L19 7"
            stroke={C.ink}
            strokeWidth="2.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </button>
  );
}

function CalendarGlyph({ tone }: { tone: string }) {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="3.5" y="5" width="17" height="15.5" rx="3" stroke={tone} strokeWidth="1.8" />
      <path d="M3.5 10h17M8.5 3.5V6M15.5 3.5V6" stroke={tone} strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      width="9"
      height="6"
      viewBox="0 0 10 6"
      fill="none"
      aria-hidden
      style={{ transform: open ? "rotate(180deg)" : undefined, transition: "transform 120ms" }}
    >
      <path d="M1 1l4 4 4-4" stroke={C.sub2} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
